import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Send, FileText, CreditCard, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { capi, formatApiError, clearCustomerSession } from "@/lib/api";

const SQUARE_LINK = "https://square.link/u/FlI5wfG1";

const fmtDT = (iso) =>
  new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Australia/Sydney" });
const fmtD = (iso) => new Date(iso).toLocaleDateString("en-AU", { timeZone: "Australia/Sydney" });

const Bubble = ({ mine, label, body, at, testid }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={testid}>
    <div className={`max-w-[85%] px-5 py-4 text-sm leading-relaxed ${
      mine ? "bg-[#C5A059]/10 border border-[#C5A059]/30" : "bg-white border border-[#C5A059]/25"}`}>
      <p className="text-[9px] tracking-[0.25em] uppercase text-[#9E7B3A] mb-1.5">{label}</p>
      <p className="whitespace-pre-wrap text-[#1A1A1A]">{body}</p>
      <p className="text-[10px] text-[#4A4A4A] mt-2">{fmtDT(at)}</p>
    </div>
  </div>
);

const ActionCard = ({ icon: Icon, title, lines, at, dateOnly, action, testid }) => (
  <div className="flex justify-center" data-testid={testid}>
    <div className="text-center px-8 py-5 border border-[#C5A059]/40 bg-[#C5A059]/5 max-w-sm w-full">
      <Icon className="w-4 h-4 text-[#9E7B3A] mx-auto mb-2" />
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A]">{title}</p>
      {lines.map((l, i) => (
        <p key={i} className={`mt-1 ${i === 0 ? "text-sm font-medium text-[#1A1A1A]" : "text-xs text-[#4A4A4A]"}`}>{l}</p>
      ))}
      {action}
      <p className="text-[10px] text-[#4A4A4A] mt-2">{dateOnly ? fmtD(at) : fmtDT(at)}</p>
    </div>
  </div>
);

const EventView = ({ event }) => {
  const { type, body, meta = {}, occurred_at } = event;
  const tid = `msg-${event.id}`;
  if (type === "enquiry") return <Bubble mine label="You" body={body} at={occurred_at} testid={tid} />;
  if (type === "reply") return <Bubble mine={false} label="Australian Luxe" body={body} at={occurred_at} testid={tid} />;
  if (type === "payment_link")
    return <ActionCard icon={CreditCard} title="Payment Link" testid={tid} at={occurred_at}
      lines={[`$${(meta.amount ?? 0).toFixed(2)}${meta.order_number ? ` · Order ${meta.order_number}` : ""}`, body]}
      action={<a href={SQUARE_LINK} target="_blank" rel="noopener noreferrer" data-testid={`pay-${event.id}`}
        className="inline-block mt-3 bg-[#1A1A1A] text-white text-[10px] tracking-[0.25em] uppercase px-6 py-2.5 hover:bg-[#9E7B3A] transition-colors duration-300">Pay Securely</a>} />;
  if (type === "payment")
    return <ActionCard icon={CreditCard} title="Payment Received" dateOnly testid={tid} at={occurred_at}
      lines={[`$${(meta.amount ?? 0).toFixed(2)} — thank you`, body || "Payment status: PAID"]} />;
  if (type === "invoice")
    return <ActionCard icon={FileText} title="Invoice" testid={tid} at={occurred_at}
      lines={[meta.order_number ? `INV-${meta.order_number}` : body]}
      action={meta.order_number && <Link to={`/account/invoice/${meta.order_number}`} data-testid={`invoice-link-${event.id}`}
        className="inline-block mt-3 border border-[#9E7B3A] text-[#9E7B3A] text-[10px] tracking-[0.25em] uppercase px-6 py-2.5 hover:bg-[#9E7B3A] hover:text-white transition-colors duration-300">View Invoice</Link>} />;
  if (type === "booking")
    return <ActionCard icon={CalendarCheck} title="Booking" testid={tid} at={occurred_at} lines={[body]} />;
  return null;
};

export default function Account() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = () => capi.get("/customer/me").then(({ data }) => setData(data)).catch((err) => {
    if (err?.response?.status === 401) { clearCustomerSession(); navigate("/account/login"); }
  });
  useEffect(() => { load(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.events?.length]);

  if (!data) return <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center">
    <p className="text-xs tracking-[0.3em] uppercase text-[#9E7B3A]">Loading…</p></div>;

  const { customer, events, bookings } = data;

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await capi.post("/customer/messages", { body: msg.trim() });
      setMsg("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const signOut = () => { clearCustomerSession(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="customer-account-page">
      <header className="bg-white border-b border-[#C5A059]/25 px-6 md:px-12 py-5 flex items-center justify-between sticky top-0 z-10">
        <Link to="/" className="flex items-center gap-3" data-testid="account-home-link">
          <img src="/assets/logo.png" alt="Australian Luxe" className="w-9 h-9 rounded-full" />
          <div>
            <p className="font-serif text-sm leading-none">Australian Luxe</p>
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#9E7B3A] mt-0.5">Your Account</p>
          </div>
        </Link>
        <div className="flex items-center gap-5">
          <p className="text-sm text-[#4A4A4A] hidden sm:block" data-testid="account-customer-name">{customer.name}</p>
          <button onClick={signOut} data-testid="customer-logout-button"
            className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] hover:text-[#9E7B3A] transition-colors duration-300">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {bookings.length > 0 && (
          <div className="mb-8" data-testid="account-bookings">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-3">Your Bookings</p>
            <div className="space-y-2">
              {bookings.map((b) => (
                <div key={b.id} className="bg-white border border-[#C5A059]/25 px-5 py-4 text-sm flex flex-wrap justify-between gap-2"
                  data-testid={`account-booking-${b.id}`}>
                  <span className="font-medium">{b.service}</span>
                  <span className="text-[#4A4A4A]">{b.preferred || "Time to be arranged"} · {b.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-3">Your Conversation</p>
        <div className="bg-white/60 border border-[#C5A059]/25 p-4 md:p-6 space-y-4 min-h-[300px]" data-testid="customer-messenger">
          {events.length === 0 && (
            <p className="text-sm text-[#4A4A4A] text-center py-10" data-testid="messenger-empty">
              Say hello — we reply to every message personally.
            </p>
          )}
          {events.map((ev) => <EventView key={ev.id} event={ev} />)}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={send} className="mt-4 flex gap-3" data-testid="customer-composer">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="customer-message-input"
            placeholder="Write a message…" aria-label="Message"
            className="flex-1 bg-white border border-[#C5A059]/30 px-5 py-3.5 text-sm placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#9E7B3A] transition-colors duration-300" />
          <button type="submit" disabled={sending || !msg.trim()} data-testid="customer-message-send"
            className="flex items-center gap-2 bg-[#1A1A1A] text-white text-[10px] tracking-[0.25em] uppercase px-6 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </form>
      </main>
    </div>
  );
}
